import { useState, useEffect } from 'react';
import { 
  FlaskConical, 
  Play, 
  Send, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Loader2,
  FileText,
  DollarSign,
  User,
  Hash,
  Wifi,
  ArrowRight,
  RefreshCw,
  Zap,
  Search,
  Sparkles,
  Plus,
  Trash2,
  Package,
  Timer,
  Gauge,
  TrendingUp,
  BarChart3,
  History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample SMS templates
const SMS_TEMPLATES = {
  mtn_success: {
    network: "MTN",
    message: "You have received LRD 500.00 from John Doe. Ref: FL-20260314-ABC123. Balance: LRD 2,500.00."
  },
  mtn_no_ref: {
    network: "MTN",
    message: "MTN MoMo: You have received 1,250.00 LRD from Jane Smith (0886123456). Fee: 0.00 LRD. Balance: 5,000.00 LRD"
  },
  orange_success: {
    network: "Orange",
    message: "Orange Money: Payment received: LRD 750.00 from Acme Corp. Reference: FL-20260314-XYZ789. Transaction successful."
  },
  orange_partial: {
    network: "Orange",
    message: "OM Transfer: 2000 LRD received. From: 0770456789. Thank you for using Orange Money."
  }
};

export const TestSMS = () => {
  const { user } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [network, setNetwork] = useState('MTN');
  const [message, setMessage] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [parseResult, setParseResult] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  // Transaction Lookup state
  const [showLookup, setShowLookup] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [lookupSearch, setLookupSearch] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Batch Creation state
  const [showBatchCreate, setShowBatchCreate] = useState(false);
  const [batchCount, setBatchCount] = useState(5);
  const [batchMinAmount, setBatchMinAmount] = useState(100);
  const [batchMaxAmount, setBatchMaxAmount] = useState(5000);
  const [batchNetworks, setBatchNetworks] = useState(['MTN', 'Orange']);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  // Auto-confirm state
  const [isAutoConfirming, setIsAutoConfirming] = useState(false);
  const [autoConfirmResult, setAutoConfirmResult] = useState(null);

  // Performance history state
  const [showPerformanceHistory, setShowPerformanceHistory] = useState(false);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Fetch pending transactions when lookup is opened
  useEffect(() => {
    if (showLookup && pendingTransactions.length === 0) {
      fetchPendingTransactions();
    }
  }, [showLookup]);

  // Fetch performance history when panel is opened
  useEffect(() => {
    if (showPerformanceHistory) {
      fetchPerformanceHistory();
    }
  }, [showPerformanceHistory]);

  const fetchPerformanceHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/admin/sms/performance-history`, {
        withCredentials: true
      });
      setPerformanceHistory(response.data);
    } catch (error) {
      toast.error('Failed to load performance history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const clearPerformanceHistory = async () => {
    if (!confirm('Clear all performance history data?')) return;
    
    try {
      await axios.delete(`${API}/admin/sms/performance-history`, {
        withCredentials: true
      });
      setPerformanceHistory([]);
      toast.success('Performance history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const fetchPendingTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/admin/sms/pending-transactions`, {
        withCredentials: true
      });
      setPendingTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load pending transactions');
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const createBatchTransactions = async () => {
    setIsCreatingBatch(true);
    setBatchResult(null);
    try {
      const response = await axios.post(`${API}/admin/sms/batch-transactions`, {
        count: batchCount,
        min_amount: batchMinAmount,
        max_amount: batchMaxAmount,
        networks: batchNetworks
      }, { withCredentials: true });
      
      setBatchResult(response.data);
      toast.success(`Created ${response.data.count} test transactions`);
      
      // Refresh pending transactions list
      fetchPendingTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch transactions');
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const deleteBatchTransactions = async () => {
    if (!confirm('Are you sure you want to delete all batch test transactions?')) return;
    
    setIsDeletingBatch(true);
    try {
      const response = await axios.delete(`${API}/admin/sms/batch-transactions`, {
        withCredentials: true
      });
      
      toast.success(`Deleted ${response.data.deleted_count} batch transactions`);
      setBatchResult(null);
      
      // Refresh pending transactions list
      fetchPendingTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch transactions');
    } finally {
      setIsDeletingBatch(false);
    }
  };

  const toggleBatchNetwork = (network) => {
    if (batchNetworks.includes(network)) {
      if (batchNetworks.length > 1) {
        setBatchNetworks(batchNetworks.filter(n => n !== network));
      }
    } else {
      setBatchNetworks([...batchNetworks, network]);
    }
  };

  const autoConfirmAll = async () => {
    if (!confirm('This will auto-confirm ALL pending batch transactions. Continue?')) return;
    
    setIsAutoConfirming(true);
    setAutoConfirmResult(null);
    try {
      const response = await axios.post(`${API}/admin/sms/auto-confirm-batch`, {}, {
        withCredentials: true
      });
      
      setAutoConfirmResult(response.data);
      
      if (response.data.confirmed > 0) {
        toast.success(`Auto-confirmed ${response.data.confirmed} transactions!`);
      } else {
        toast.info('No pending batch transactions to confirm');
      }
      
      // Refresh pending transactions list and performance history
      fetchPendingTransactions();
      if (showPerformanceHistory) {
        fetchPerformanceHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-confirm transactions');
    } finally {
      setIsAutoConfirming(false);
    }
  };

  const generateSMSFromTransaction = async (transactionId) => {
    setIsGenerating(true);
    try {
      const response = await axios.get(`${API}/admin/sms/generate-test-sms/${transactionId}`, {
        withCredentials: true
      });
      
      setNetwork(response.data.network);
      setMessage(response.data.generated_sms);
      setParseResult(null);
      setMatchResult(null);
      setSubmitResult(null);
      setShowLookup(false);
      
      toast.success(`Generated SMS for transaction ${transactionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate SMS');
    } finally {
      setIsGenerating(false);
    }
  };

  const filteredTransactions = pendingTransactions.filter(tx => 
    tx.transaction_id?.toLowerCase().includes(lookupSearch.toLowerCase()) ||
    tx.reference?.toLowerCase().includes(lookupSearch.toLowerCase()) ||
    tx.customer_name?.toLowerCase().includes(lookupSearch.toLowerCase())
  );

  const loadTemplate = (templateKey) => {
    const template = SMS_TEMPLATES[templateKey];
    if (template) {
      setNetwork(template.network);
      setMessage(template.message);
      setParseResult(null);
      setMatchResult(null);
      setSubmitResult(null);
    }
  };

  const handleParse = async () => {
    if (!message.trim()) {
      toast.error('Please enter an SMS message');
      return;
    }

    setIsParsing(true);
    setParseResult(null);
    setMatchResult(null);
    setSubmitResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/sms/test-parse`,
        { message, network },
        { withCredentials: true }
      );
      setParseResult(response.data);
      toast.success('SMS parsed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse SMS');
    } finally {
      setIsParsing(false);
    }
  };

  const handleMatch = async () => {
    if (!message.trim()) {
      toast.error('Please enter an SMS message');
      return;
    }

    setIsMatching(true);
    setMatchResult(null);
    setSubmitResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/sms/test-match`,
        { message, network },
        { withCredentials: true }
      );
      setMatchResult(response.data);
      
      if (response.data.matched_transaction) {
        toast.success(`Found matching transaction: ${response.data.matched_transaction.transaction_id}`);
      } else if (response.data.potential_matches?.length > 0) {
        toast.info(`Found ${response.data.potential_matches.length} potential matches`);
      } else {
        toast.warning('No matching transactions found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run matching');
    } finally {
      setIsMatching(false);
    }
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter an SMS message');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${API}/admin/sms/test-submit`,
        { message, network },
        { withCredentials: true }
      );
      setSubmitResult(response.data);
      
      if (response.data.matched_transaction) {
        toast.success(`Transaction ${response.data.matched_transaction.transaction_id} confirmed!`);
      } else {
        toast.info('SMS added to manual review queue');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit SMS');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setMessage('');
    setParseResult(null);
    setMatchResult(null);
    setSubmitResult(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'partial':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="test-sms-page">
      <Toaster richColors position="top-right" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-xl font-semibold flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <FlaskConical className="w-6 h-6 text-purple-600" />
            Test SMS Tool
          </h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            Test SMS parsing and transaction matching without real GSM hardware
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resetAll}
          className={isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          {/* SMS Input */}
          <div className={`rounded-xl p-6 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <FileText className={isDarkMode ? 'w-5 h-5 text-slate-400' : 'w-5 h-5 text-gray-500'} />
              SMS Input
            </h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Network</Label>
                <Select value={network} onValueChange={setNetwork}>
                  <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} data-testid="network-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
                    <SelectItem value="MTN" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                        MTN Mobile Money
                      </span>
                    </SelectItem>
                    <SelectItem value="Orange" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                        Orange Money
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Raw SMS Message</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Paste your SMS message here..."
                  className={`min-h-[120px] ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`}
                  data-testid="sms-input"
                />
              </div>

              {/* Template Buttons */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Quick Templates</Label>
                  <button
                    onClick={() => setShowLookup(!showLookup)}
                    className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors"
                    data-testid="lookup-toggle"
                  >
                    <Search className="w-3 h-3" />
                    {showLookup ? 'Hide Lookup' : 'Transaction Lookup'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => loadTemplate('mtn_success')}
                    className="px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs hover:bg-yellow-100 transition-colors border border-yellow-200"
                  >
                    MTN Complete
                  </button>
                  <button
                    onClick={() => loadTemplate('mtn_no_ref')}
                    className="px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs hover:bg-yellow-100 transition-colors border border-yellow-200"
                  >
                    MTN No Ref
                  </button>
                  <button
                    onClick={() => loadTemplate('orange_success')}
                    className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs hover:bg-orange-100 transition-colors border border-orange-200"
                  >
                    Orange Complete
                  </button>
                  <button
                    onClick={() => loadTemplate('orange_partial')}
                    className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs hover:bg-orange-100 transition-colors border border-orange-200"
                  >
                    Orange Partial
                  </button>
                </div>
              </div>

              {/* Transaction Lookup Panel */}
              {showLookup && (
                <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`} data-testid="lookup-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Search className="w-4 h-4 text-teal-600" />
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Transaction Lookup</h4>
                  </div>
                  <p className={`text-xs mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Select a pending transaction to auto-generate a matching SMS for testing.
                  </p>
                  
                  <div className="mb-3">
                    <Input
                      placeholder="Search by ID, reference, or customer..."
                      value={lookupSearch}
                      onChange={(e) => setLookupSearch(e.target.value)}
                      className={`text-sm ${isDarkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      data-testid="lookup-search"
                    />
                  </div>

                  {isLoadingTransactions ? (
                    <div className={`flex items-center justify-center py-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Loading transactions...
                    </div>
                  ) : filteredTransactions.length === 0 ? (
                    <div className="text-center py-6">
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {pendingTransactions.length === 0 
                          ? 'No pending transactions found' 
                          : 'No transactions match your search'}
                      </p>
                      <button
                        onClick={fetchPendingTransactions}
                        className="text-teal-600 text-xs mt-2 hover:underline"
                      >
                        Refresh
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {filteredTransactions.map((tx) => (
                        <div
                          key={tx.transaction_id}
                          className={`rounded-lg p-3 flex items-center justify-between transition-colors border ${isDarkMode ? 'bg-slate-600 hover:bg-slate-500 border-slate-500' : 'bg-white hover:bg-gray-50 border-gray-100'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-mono text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {tx.transaction_id}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                tx.network === 'MTN' 
                                  ? 'bg-yellow-50 text-yellow-700' 
                                  : 'bg-orange-50 text-orange-700'
                              }`}>
                                {tx.network}
                              </span>
                            </div>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              LRD {tx.amount?.toLocaleString()} • {tx.customer_name || 'Unknown'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => generateSMSFromTransaction(tx.transaction_id)}
                            disabled={isGenerating}
                            className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                            data-testid={`generate-btn-${tx.transaction_id}`}
                          >
                            {isGenerating ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3 mr-1" />
                                Generate
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Batch Transaction Creation */}
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-600" />
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Batch Test Transactions</h4>
                  </div>
                  <button
                    onClick={() => setShowBatchCreate(!showBatchCreate)}
                    className="text-xs text-purple-600 hover:text-purple-700 transition-colors"
                    data-testid="batch-toggle"
                  >
                    {showBatchCreate ? 'Hide' : 'Show Options'}
                  </button>
                </div>

                {showBatchCreate ? (
                  <div className="space-y-4">
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Create multiple pending transactions for comprehensive SMS confirmation testing.
                    </p>

                    {/* Count */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Number of Transactions</Label>
                        <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{batchCount}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={batchCount}
                        onChange={(e) => setBatchCount(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        data-testid="batch-count-slider"
                      />
                      <div className={`flex justify-between text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                        <span>1</span>
                        <span>20</span>
                      </div>
                    </div>

                    {/* Amount Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Min Amount (LRD)</Label>
                        <Input
                          type="number"
                          value={batchMinAmount}
                          onChange={(e) => setBatchMinAmount(parseFloat(e.target.value) || 0)}
                          className={`text-sm h-9 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          data-testid="batch-min-amount"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Max Amount (LRD)</Label>
                        <Input
                          type="number"
                          value={batchMaxAmount}
                          onChange={(e) => setBatchMaxAmount(parseFloat(e.target.value) || 0)}
                          className={`text-sm h-9 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          data-testid="batch-max-amount"
                        />
                      </div>
                    </div>

                    {/* Networks */}
                    <div className="space-y-2">
                      <Label className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Networks</Label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleBatchNetwork('MTN')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            batchNetworks.includes('MTN')
                              ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                              : isDarkMode ? 'bg-slate-700 text-slate-400 border border-slate-600' : 'bg-white text-gray-400 border border-gray-200'
                          }`}
                          data-testid="batch-network-mtn"
                        >
                          MTN
                        </button>
                        <button
                          onClick={() => toggleBatchNetwork('Orange')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            batchNetworks.includes('Orange')
                              ? 'bg-orange-100 text-orange-700 border border-orange-300'
                              : isDarkMode ? 'bg-slate-700 text-slate-400 border border-slate-600' : 'bg-white text-gray-400 border border-gray-200'
                          }`}
                          data-testid="batch-network-orange"
                        >
                          Orange
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={createBatchTransactions}
                        disabled={isCreatingBatch}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="create-batch-btn"
                      >
                        {isCreatingBatch ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Create {batchCount}
                      </Button>
                      <Button
                        onClick={autoConfirmAll}
                        disabled={isAutoConfirming}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="auto-confirm-btn"
                      >
                        {isAutoConfirming ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Auto-Confirm All
                      </Button>
                      <Button
                        onClick={deleteBatchTransactions}
                        disabled={isDeletingBatch}
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="delete-batch-btn"
                      >
                        {isDeletingBatch ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Batch Result */}
                    {batchResult && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-emerald-700 text-sm font-medium">
                          Created {batchResult.count} transactions
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                          Click "Auto-Confirm All" to confirm them instantly
                        </p>
                      </div>
                    )}

                    {/* Auto-Confirm Result */}
                    {autoConfirmResult && (
                      <div className={`rounded-lg p-3 ${
                        autoConfirmResult.confirmed > 0 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-amber-50 border border-amber-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {autoConfirmResult.confirmed > 0 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                          )}
                          <p className={`font-medium ${
                            autoConfirmResult.confirmed > 0 ? 'text-emerald-700' : 'text-amber-700'
                          }`}>
                            {autoConfirmResult.message}
                          </p>
                        </div>
                        {autoConfirmResult.confirmed > 0 && (
                          <>
                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Processed</p>
                                <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{autoConfirmResult.processed}</p>
                              </div>
                              <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Confirmed</p>
                                <p className="text-emerald-600 font-bold text-lg">{autoConfirmResult.confirmed}</p>
                              </div>
                              <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Failed</p>
                                <p className={`font-bold text-lg ${autoConfirmResult.failed > 0 ? 'text-red-500' : isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                  {autoConfirmResult.failed}
                                </p>
                              </div>
                            </div>

                            {/* Performance Metrics */}
                            {autoConfirmResult.metrics && (
                              <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Gauge className="w-4 h-4 text-cyan-600" />
                                  <p className="text-cyan-700 text-xs font-medium">Performance Metrics</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className={`rounded p-2 flex items-center gap-2 border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                    <Timer className={isDarkMode ? 'w-3 h-3 text-slate-500' : 'w-3 h-3 text-gray-400'} />
                                    <div>
                                      <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Total Time</p>
                                      <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {autoConfirmResult.metrics.total_time_ms < 1000 
                                          ? `${autoConfirmResult.metrics.total_time_ms.toFixed(0)}ms`
                                          : `${(autoConfirmResult.metrics.total_time_ms / 1000).toFixed(2)}s`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className={`rounded p-2 flex items-center gap-2 border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                    <TrendingUp className={isDarkMode ? 'w-3 h-3 text-slate-500' : 'w-3 h-3 text-gray-400'} />
                                    <div>
                                      <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Throughput</p>
                                      <p className="text-cyan-600 font-mono font-bold">
                                        {autoConfirmResult.metrics.transactions_per_second.toFixed(1)} tx/s
                                      </p>
                                    </div>
                                  </div>
                                  <div className={`rounded p-2 flex items-center gap-2 border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                    <Zap className={isDarkMode ? 'w-3 h-3 text-slate-500' : 'w-3 h-3 text-gray-400'} />
                                    <div>
                                      <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Avg per TX</p>
                                      <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {autoConfirmResult.metrics.avg_time_per_tx_ms.toFixed(1)}ms
                                      </p>
                                    </div>
                                  </div>
                                  <div className={`rounded p-2 flex items-center gap-2 border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                                    <ArrowRight className={isDarkMode ? 'w-3 h-3 text-slate-500' : 'w-3 h-3 text-gray-400'} />
                                    <div>
                                      <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Min / Max</p>
                                      <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {autoConfirmResult.metrics.min_time_ms.toFixed(0)} / {autoConfirmResult.metrics.max_time_ms.toFixed(0)}ms
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Quickly create multiple test transactions
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleParse}
                  disabled={isParsing || !message.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="parse-btn"
                >
                  {isParsing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Parse SMS
                </Button>
                <Button
                  onClick={handleMatch}
                  disabled={isMatching || !message.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="match-btn"
                >
                  {isMatching ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Find Matches
                </Button>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className={`rounded-xl p-4 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>How It Works</h3>
            <div className={`space-y-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-purple-600 text-xs font-bold">1</span>
                </div>
                <span><strong className={isDarkMode ? 'text-white' : 'text-gray-700'}>Parse SMS</strong> - Extract amount, sender, and reference</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-xs font-bold">2</span>
                </div>
                <span><strong className={isDarkMode ? 'text-white' : 'text-gray-700'}>Find Matches</strong> - Search for pending transactions</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-xs font-bold">3</span>
                </div>
                <span><strong className={isDarkMode ? 'text-white' : 'text-gray-700'}>Submit</strong> - Confirm transaction or queue for review</span>
              </div>
            </div>
          </div>

          {/* Performance History */}
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-cyan-900/30 border-cyan-700' : 'bg-cyan-50 border-cyan-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-cyan-600" />
                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Performance History</h4>
              </div>
              <button
                onClick={() => setShowPerformanceHistory(!showPerformanceHistory)}
                className="text-xs text-cyan-600 hover:text-cyan-700 transition-colors"
                data-testid="perf-history-toggle"
              >
                {showPerformanceHistory ? 'Hide' : 'Show Chart'}
              </button>
            </div>

            {showPerformanceHistory ? (
              <div className="space-y-4">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Track throughput across multiple auto-confirm runs.
                </p>

                {isLoadingHistory ? (
                  <div className={`flex items-center justify-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Loading history...
                  </div>
                ) : performanceHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <History className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>No performance data yet</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Run Auto-Confirm to start tracking</p>
                  </div>
                ) : (
                  <>
                    {/* Chart */}
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
                      <ResponsiveContainer width="100%" height={180}>
                        <ComposedChart data={performanceHistory}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#475569' : '#e5e7eb'} />
                          <XAxis 
                            dataKey="timestamp" 
                            tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: 10 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
                            }}
                          />
                          <YAxis 
                            yAxisId="left"
                            tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: 10 }}
                            label={{ value: 'tx/s', angle: -90, position: 'insideLeft', fill: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: 10 }}
                          />
                          <YAxis 
                            yAxisId="right" 
                            orientation="right"
                            tick={{ fill: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: 10 }}
                            label={{ value: 'count', angle: 90, position: 'insideRight', fill: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: 10 }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                              border: isDarkMode ? '1px solid #475569' : '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '12px',
                              color: isDarkMode ? '#f1f5f9' : '#111827'
                            }}
                            labelFormatter={(value) => new Date(value).toLocaleString()}
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="confirmed" 
                            fill="#10b981" 
                            name="Confirmed"
                            radius={[4, 4, 0, 0]}
                            opacity={0.6}
                          />
                          <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="transactions_per_second" 
                            stroke="#0891b2" 
                            strokeWidth={2}
                            dot={{ fill: '#0891b2', r: 4 }}
                            name="Throughput (tx/s)"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Total Runs</p>
                        <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{performanceHistory.length}</p>
                      </div>
                      <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Avg Throughput</p>
                        <p className="text-cyan-600 font-bold">
                          {(performanceHistory.reduce((sum, h) => sum + h.transactions_per_second, 0) / performanceHistory.length).toFixed(1)} tx/s
                        </p>
                      </div>
                      <div className={`rounded p-2 text-center border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Peak</p>
                        <p className="text-emerald-600 font-bold">
                          {Math.max(...performanceHistory.map(h => h.transactions_per_second)).toFixed(1)} tx/s
                        </p>
                      </div>
                    </div>

                    {/* Clear Button */}
                    <button
                      onClick={clearPerformanceHistory}
                      className={`w-full text-xs py-2 transition-colors ${isDarkMode ? 'text-slate-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                    >
                      Clear History
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                View throughput trends over time
              </p>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {/* Parse Result */}
          {parseResult && (
            <div className={`rounded-xl p-6 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} data-testid="parse-result">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {getStatusIcon(parseResult.parsing_status)}
                  Parsing Result
                </h2>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(parseResult.parsing_status)}`}>
                  {parseResult.parsing_status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <DollarSign className="w-4 h-4" />
                    Amount
                  </div>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {parseResult.parsed_amount !== null ? (
                      <>LRD {parseResult.parsed_amount.toLocaleString()}</>
                    ) : (
                      <span className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>Not found</span>
                    )}
                  </p>
                </div>

                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <User className="w-4 h-4" />
                    Sender
                  </div>
                  <p className={`text-lg font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {parseResult.parsed_sender || <span className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>Not found</span>}
                  </p>
                </div>

                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Hash className="w-4 h-4" />
                    Reference
                  </div>
                  <p className={`text-lg font-medium font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {parseResult.parsed_reference || <span className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>Not found</span>}
                  </p>
                </div>

                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Wifi className="w-4 h-4" />
                    Network
                  </div>
                  <p className="text-lg font-medium">
                    <span className={`${parseResult.network === 'MTN' ? 'text-yellow-600' : 'text-orange-600'}`}>
                      {parseResult.network}
                    </span>
                  </p>
                </div>
              </div>

              {parseResult.errors?.length > 0 && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm font-medium mb-1">Parsing Issues:</p>
                  <ul className="text-red-600 text-sm space-y-1">
                    {parseResult.errors.map((error, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <XCircle className="w-3 h-3" />
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Match Result */}
          {matchResult && (
            <div className={`rounded-xl p-6 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} data-testid="match-result">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Zap className="w-5 h-5 text-blue-500" />
                  Matching Result
                </h2>
                {matchResult.matched_transaction ? (
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
                    Auto-Match Found
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-medium">
                    Manual Review
                  </span>
                )}
              </div>

              {matchResult.matched_transaction ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <p className="text-emerald-700 font-medium mb-2">Exact Match Found!</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Transaction ID:</span>
                      <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{matchResult.matched_transaction.transaction_id}</p>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Reference:</span>
                      <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{matchResult.matched_transaction.reference}</p>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Amount:</span>
                      <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>LRD {matchResult.matched_transaction.amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Merchant:</span>
                      <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{matchResult.matched_transaction.merchant_name}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-amber-700 font-medium">No exact match found</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    This SMS would be queued for manual review.
                  </p>
                </div>
              )}

              {matchResult.potential_matches?.length > 0 && (
                <div>
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Potential Matches ({matchResult.potential_matches.length})</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {matchResult.potential_matches.map((tx, i) => (
                      <div key={i} className={`rounded-lg p-3 flex items-center justify-between ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                        <div className="flex-1 min-w-0">
                          <p className={`font-mono text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tx.transaction_id}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                            LRD {tx.amount.toLocaleString()} • {tx.reference}
                          </p>
                        </div>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          tx.match_score >= 100 ? 'bg-emerald-50 text-emerald-700' :
                          tx.match_score >= 50 ? 'bg-amber-50 text-amber-700' :
                          isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tx.match_score}% match
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send to Matching Engine
              </Button>
            </div>
          )}

          {/* Submit Result */}
          {submitResult && (
            <div className={`rounded-xl p-6 border ${
              submitResult.matched_transaction 
                ? 'bg-emerald-50 border-emerald-200' 
                : 'bg-blue-50 border-blue-200'
            }`} data-testid="submit-result">
              <div className="flex items-center gap-3 mb-4">
                {submitResult.matched_transaction ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-blue-500" />
                )}
                <div>
                  <h3 className={`text-lg font-semibold ${
                    submitResult.matched_transaction ? 'text-emerald-700' : 'text-blue-700'
                  }`}>
                    {submitResult.matched_transaction ? 'Transaction Confirmed!' : 'Added to Review Queue'}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    SMS ID: <span className="font-mono">{submitResult.sms_id}</span>
                  </p>
                </div>
              </div>

              {submitResult.matched_transaction && (
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-100'}`}>
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Confirmed Transaction:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>ID:</span>
                      <span className={`ml-2 font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{submitResult.matched_transaction.transaction_id}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Amount:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>LRD {submitResult.matched_transaction.amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!parseResult && !matchResult && !submitResult && (
            <div className={`rounded-xl p-12 border text-center shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <FlaskConical className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
              <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Ready to Test</h3>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                Enter an SMS message and click "Parse SMS" to see the extracted data
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestSMS;
